import React, { useEffect } from "react";
import Navbar from "../components/layout/Navbar";
import Footer from "../components/layout/Footer";
import { Shield, AlertTriangle, Mail } from "lucide-react";
import { useAuth } from "../lib/auth";
import { FEE_DEFAULT, TEAM_MIN_DEFAULT, TEAM_MAX_DEFAULT } from "../lib/appDefaults";

const makeSections = (fee, minTeam, maxTeam, preLaunch, prizePool) => [
  {
    id: "eligibility",
    title: "Eligibility",
    body: (
      <>
        <p>Participation is open to any currently-enrolled student with a valid institutional college email address.</p>
        <p>Personal email providers — including but not limited to <span className="text-white">Gmail, Yahoo, Outlook, Hotmail, Rediffmail, iCloud, and Live</span> — are <span className="text-white">not accepted</span> as the verification email. Registrations made with a personal email as the institutional email will be rejected without refund.</p>
      </>
    ),
  },
  {
    id: "payment",
    title: "Payment & Refunds",
    body: (
      <>
        <p>The registration fee is {preLaunch ? <span className="text-white">a per-person amount announced when registration opens</span> : <span className="text-white">₹{fee} per person</span>}, and is <span className="text-brand">strictly non-refundable under any circumstances</span>.</p>
        <p>This includes, but is not limited to:</p>
        <ul className="list-disc pl-5 space-y-1.5">
          <li>Voluntary withdrawal from the competition</li>
          <li>Disqualification for any reason</li>
          <li>Team falling below the minimum required size</li>
          <li>Inability to submit before any deadline</li>
          <li>Any technical, personal, or third-party issue</li>
          <li>Any reason not explicitly listed above</li>
        </ul>
        <p className="font-semibold text-white">No exceptions.</p>
      </>
    ),
  },
  {
    id: "team-rules",
    title: "Team Rules",
    body: (
      <>
        <p>Teams must consist of {preLaunch ? <span className="text-white">a fixed number of members (the exact team size is announced when registration opens)</span> : <span className="text-white">{minTeam} to {maxTeam} members</span>}, all of whom must individually register, verify their institutional email, and complete payment.</p>
        <p>Team composition can be modified freely before the <span className="text-white">team lock deadline</span>. After the lock deadline, all team membership changes are <span className="text-white">frozen</span> — no additions, removals, or replacements.</p>
        <p>Teams with fewer than {preLaunch ? "the minimum required" : minTeam} accepted members at the lock deadline will be <span className="text-brand">automatically disqualified</span>, with no refund issued to any member.</p>
      </>
    ),
  },
  {
    id: "submissions",
    title: "Submission Requirements",
    body: (
      <>
        <p><span className="text-white">Round 1</span> requires a problem statement, proposed solution, tech stack, and a recorded pitch video of 2–3 minutes.</p>
        <p><span className="text-white">Round 2</span> (for shortlisted teams) requires a working prototype, a source-code repository link, a recorded pitch video of up to 5 minutes, and an optional live demo link.</p>
        <p>All submissions must be <span className="text-white">original work created during the competition period</span>. Plagiarism, copying, or use of pre-existing projects without explicit disclosure will result in <span className="text-brand">immediate disqualification with no refund</span>.</p>
      </>
    ),
  },
  {
    id: "ip",
    title: "Intellectual Property",
    body: (
      <>
        <p>Teams <span className="text-white">retain full ownership</span> of all ideas, code, designs, and projects they submit.</p>
        <p>By participating, teams grant NoobSync Innovate a <span className="text-white">non-exclusive, royalty-free license</span> to showcase, share, and promote submissions — including to potential employers, investors, mentors, and partners — for the purposes of recognition, talent visibility, and platform promotion.</p>
        <p>This license <span className="text-white">does not transfer ownership</span> and <span className="text-white">does not restrict</span> teams from using, developing, or commercializing their own work independently.</p>
      </>
    ),
  },
  {
    id: "judging",
    title: "Judging",
    body: (
      <>
        <p>All submissions are reviewed by a panel of judges across five criteria: <span className="text-white">innovation, real-world impact, feasibility, technical execution, and presentation quality</span>.</p>
        <p>Individual scores and detailed judging criteria are <span className="text-white">internal</span> and will not be shared with participants. Judging decisions are <span className="text-white">final and binding</span>.</p>
        <p>Teams will only be notified of their selection status: shortlisted / not shortlisted, winner / not winner.</p>
      </>
    ),
  },
  {
    id: "prizes",
    title: "Prizes",
    body: (
      <>
        <p>The total prize pool for this edition is <span className="text-white">{prizePool}</span>, awarded as cash prizes to the top three teams — Champion, Runner-up, and Third place. The pool and its split are fixed for the edition and shown on the public site; they are not calculated dynamically. In the rare event of a change, registered participants will be notified by email.</p>
        <p>Prize distribution is at the <span className="text-white">sole discretion</span> of NoobSync Innovate. Winners will be contacted directly via the email on file for disbursement and are responsible for any applicable taxes on prize amounts.</p>
      </>
    ),
  },
  {
    id: "conduct",
    title: "Code of Conduct",
    body: (
      <>
        <p>Participants must maintain <span className="text-white">respectful conduct</span> throughout the competition.</p>
        <p>Harassment, abusive behaviour, spamming, or submission of fake or fraudulent content directed at judges, organisers, or other participants will result in <span className="text-brand">immediate disqualification</span> without refund, and may result in a <span className="text-brand">permanent ban</span> from future NoobSync Innovate events.</p>
      </>
    ),
  },
  {
    id: "disqualification",
    title: "Disqualification",
    body: (
      <>
        <p>NoobSync Innovate reserves the right to disqualify any team or individual at any stage of the competition for violation of these terms, <span className="text-white">without refund</span> and <span className="text-white">without obligation</span> to provide detailed reasoning.</p>
      </>
    ),
  },
  {
    id: "liability",
    title: "Liability",
    body: (
      <>
        <p>NoobSync Innovate is <span className="text-white">not liable</span> for technical issues, internet connectivity problems, third-party platform failures (including YouTube, Google Drive, GitHub, payment gateways, and email delivery), or any other issues outside its direct control that may affect participation or submissions.</p>
      </>
    ),
  },
  {
    id: "changes",
    title: "Changes to Terms",
    body: (
      <>
        <p>NoobSync Innovate reserves the right to modify dates, rules, prize structures, or any aspect of the competition with <span className="text-white">reasonable notice</span> to registered participants via email.</p>
      </>
    ),
  },
  {
    id: "governing-law",
    title: "Governing Law",
    body: (
      <>
        <p>These terms are governed by the <span className="text-white">laws of India</span>. Any disputes arising shall be subject to the <span className="text-white">exclusive jurisdiction of courts in Andhra Pradesh, India</span>.</p>
      </>
    ),
  },
];

export default function Terms() {
  const { config } = useAuth();
  const fee = config?.entry_fee ?? FEE_DEFAULT;
  const minTeam = config?.min_team_size ?? TEAM_MIN_DEFAULT;
  const maxTeam = config?.max_team_size ?? TEAM_MAX_DEFAULT;
  const preLaunch = config?.status === "pre_launch";
  const prizePool = `₹${((config?.prize_1_base ?? 15000) + (config?.prize_2_base ?? 10000) + (config?.prize_3_base ?? 5000)).toLocaleString("en-IN")}`;
  const SECTIONS = makeSections(fee, minTeam, maxTeam, preLaunch, prizePool);
  useEffect(() => { window.scrollTo(0, 0); }, []);

  return (
    <div className="App" data-testid="terms-page">
      <Navbar />

      <section className="relative grid-bg border-b border-white/10">
        <div className="max-w-[1100px] mx-auto px-5 sm:px-8 lg:px-12 py-16 sm:py-20">
          <div className="label-eyebrow mb-4">
            Last updated: 19 July 2026 · Effective 2026
          </div>
          <h1 className="font-display text-5xl sm:text-7xl text-white leading-[0.95]" data-testid="terms-title">
            Terms &amp; Conditions
          </h1>
          <p className="mt-6 text-gray-400 max-w-2xl text-base sm:text-lg leading-relaxed">
            Read these carefully before registering. By creating an IdeaThon account or paying the registration fee, you agree to be bound by every section below.
          </p>
        </div>
      </section>

      <section className="bg-[#080C18]">
        <div className="max-w-[1100px] mx-auto px-5 sm:px-8 lg:px-12 py-14 grid lg:grid-cols-[260px_1fr] gap-10">
          {/* TOC */}
          <aside className="lg:sticky lg:top-20 lg:self-start" data-testid="terms-toc">
            <div className="label-eyebrow mb-3">On this page</div>
            <ul className="space-y-2 text-sm">
              {SECTIONS.map((s, i) => (
                <li key={s.id}>
                  <a href={`#${s.id}`} className="text-gray-400 hover:text-brand transition-colors flex gap-2" data-testid={`toc-${s.id}`}>
                    <span className="text-gray-600 font-mono">{String(i + 1).padStart(2, "0")}</span> {s.title}
                  </a>
                </li>
              ))}
            </ul>
          </aside>

          {/* Body */}
          <div className="space-y-10">
            <div className="card-surface border-l-2 border-brand flex items-start gap-3" data-testid="terms-banner">
              <AlertTriangle className="w-5 h-5 text-brand mt-0.5 flex-shrink-0" strokeWidth={2} />
              <p className="text-sm text-gray-300 leading-relaxed">
                <span className="text-white font-semibold">Important:</span> The <span className="text-brand">registration fee is non-refundable under any circumstance</span>{preLaunch ? "" : <> (currently <span className="text-white">₹{fee}</span>)</>}. Please be certain before paying. See <a href="#payment" className="text-brand underline underline-offset-2">Payment &amp; Refunds</a> for the full policy.
              </p>
            </div>

            {SECTIONS.map((s, i) => (
              <article id={s.id} key={s.id} className="scroll-mt-24" data-testid={`section-${s.id}`}>
                <div className="flex items-baseline gap-4 mb-4">
                  <span className="font-mono text-xs text-brand">{String(i + 1).padStart(2, "0")}</span>
                  <h2 className="font-display text-2xl sm:text-3xl text-white">{s.title}</h2>
                </div>
                <div className="text-gray-400 leading-relaxed space-y-3 pl-0 sm:pl-9 text-[15px]">
                  {s.body}
                </div>
              </article>
            ))}

            <div className="card-surface flex items-start gap-3 mt-12" data-testid="terms-contact">
              <Shield className="w-5 h-5 text-brand mt-0.5 flex-shrink-0" strokeWidth={2} />
              <div className="text-sm">
                <div className="text-white font-semibold mb-1">Questions about these terms?</div>
                <p className="text-gray-400">
                  Reach out at{" "}
                  <a href="mailto:info@noobsync.com" className="text-brand hover:underline inline-flex items-center gap-1">
                    <Mail className="w-3.5 h-3.5" /> info@noobsync.com
                  </a>
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
